/* eslint-disable no-param-reassign */
import {merge} from 'lodash';
import {createSlice} from '@reduxjs/toolkit';
import {
    getInsightsReports, getInsightsReportById, updateInsightsReportById, updateInsightsReportTextById, deleteInsightsReportById,
    bulkUpdateInsightsReports
} from '../api/insightsReportsThunk';
import global from '../constants/global';

const setPendingState = (state) => {
    state.status = merge(state.status, {
        loading: true,
        textUpdated: false,
        reportUpdated: false,
        deleted: false,
        reportsUpdated: false
    });
};

const setRejectedState = (state, action) => {
    state.status = merge(state.status, {loading: false});
    state.error = action.error.message;
};

const setGetFulfilledState = (state) => {
    state.status = merge(state.status, {
        loading: false
    });
};

const setBulkUpdateFulfilledState = (state) => {
    state.status = merge(state.status, {
        loading: false,
        error: false,
        reportsUpdated: true
    });
    state.error = null;
};

const initialState = {
    error: null,
    status: {
        loading: false, textUpdated: false, reportUpdated: false, deleted: false, reportsUpdated: false
    },
    reports: [],
    report: null,
    count: 0
};

const insightsReportsSlice = createSlice({
    name: 'insightsReports',
    initialState,
    reducers: {
        resetStatus: (state) => {
            state.status = merge(state.status, {textUpdated: false, reportUpdated: false, deleted: false});
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(getInsightsReports.fulfilled, (state, action) => {
                setGetFulfilledState(state);
                const {view, typeId} = action.meta.arg || {};
                const {rows, count} = action.payload;

                const newReports = typeId !== undefined
                    ? state.reports.filter((report) => report.typeId === typeId)
                    : state.reports;

                // Grid view is an infinite scroll, so we append the new reports to the existing ones unless we are on the first page
                // as the pagination may have been reset (e.g. when filters change).
                state.reports = (view === global.INSIGHTS_REPORTS_VIEW_TYPES.grid.value && action.meta.arg.page > 1)
                    ? [...newReports, ...rows]
                    : rows;

                state.count = count;
            })
            .addCase(getInsightsReportById.fulfilled, (state, action) => {
                setGetFulfilledState(state);
                state.report = action.payload;
            })
            .addCase(deleteInsightsReportById.fulfilled, (state) => {
                setGetFulfilledState(state);
                state.status.deleted = true;
                state.report = null;
            })
            .addCase(updateInsightsReportTextById.fulfilled, (state, action) => {
                setGetFulfilledState(state);
                state.status.textUpdated = true;
                const [, [updatedText]] = action.payload;
                state.report.text = state.report.text.map((text) => (text.id === updatedText.id ? updatedText : text));
            })
            .addCase(updateInsightsReportById.fulfilled, (state, action) => {
                setGetFulfilledState(state);
                state.status.reportUpdated = true;
                const [, [updatedReport]] = action.payload;
                state.report = merge(state.report, updatedReport);
            })
            // eslint-disable-next-line no-unused-vars
            .addCase(bulkUpdateInsightsReports.fulfilled, (state, action) => {
                setBulkUpdateFulfilledState(state);
            })
            .addMatcher(
                (action) => action.type.endsWith('/pending') && action.type.includes('insightsReports'),
                setPendingState
            )
            .addMatcher(
                (action) => action.type.endsWith('/rejected') && action.type.includes('insightsReports'),
                setRejectedState
            );
    }
});

export const {resetStatus} = insightsReportsSlice.actions;
export default insightsReportsSlice.reducer;
